import { IS_WEB } from 'constants/platform'
import React, { useEffect, useMemo, useState } from 'react'

import { EditProfileForm } from 'partials/EditProfileForm'
import { EditableAvatar } from 'partials/EditableAvatar'
import { OnBoardingView } from 'partials/OnBoarding'
import { Page } from 'partials/Page'

import { baseCrop, defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Box } from 'widgets/Box'
import { Separator } from 'widgets/Separator'

import { useAvatarUpload } from 'hooks/useAvatarUpload'
import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCoachReferral } from 'hooks/useCoachReferral'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useGroup } from 'hooks/useGroup'
import { usePendingMembership } from 'hooks/usePendingMembership'
import { usePendingMemberships } from 'hooks/usePendingMemberships'

import { links } from 'utils/constants'

import maskedBgImage from 'assets/images/bg-mask-gray.png'

import { UserType } from 'types/user'

import { AvatarContainer } from './styled'

interface ProfilePageProps {
  navigation?: any
  onboardingView?: boolean
}

export const EditProfilePage = ({ navigation, onboardingView = false }: ProfilePageProps) => {
  const [showBusyIndicator, setShowBusyIndicator] = useState(false)

  const { data, update, refetch } = useCurrentUser()
  const { mutateAsync: uploadAvatar } = useAvatarUpload()

  const currentUser = data
  const { user, group } = useCoachReferral()

  const { meta } = useGroup({ userId: user?.id })
  const { hasSetupGroup } = meta

  const { data: subscriptions } = useClientSubscriptions({
    userId: currentUser?.id,
  })

  const isSubscribed = useMemo(() => {
    if (!hasSetupGroup) return null

    return subscriptions?.find((subscription) => subscription.program.trainer.id === user?.id)
  }, [subscriptions, user?.id, hasSetupGroup])

  const { addPendingMembership } = usePendingMembership({
    userId: currentUser?.id,
    program: group,
    trainer: user,
  })

  const { data: pendingMemberships } = usePendingMemberships({ userId: currentUser?.id })
  const pendingPrograms = pendingMemberships ? pendingMemberships.map((pm) => pm.program) : []
  const isPendingMembership: boolean | undefined = pendingPrograms?.some(
    (p) => p?.trainer?.id === user?.id,
  )

  useEffect(() => {
    refetch()
  }, [refetch])

  const { isChallengeToggleEnabled } = useFeatureFlags()

  const addChallengeMembership = async () => {
    if (!isPendingMembership && !isSubscribed && user && group) {
      await addPendingMembership.mutateAsync()
    }
  }

  const handleSubmit = async (values: any, skipAvatar?: boolean) => {
    setShowBusyIndicator(true)

    let avatar = data?.avatar

    if (skipAvatar !== null && skipAvatar) {
      avatar = undefined
    }

    // Upload the default avatar for this user if they haven't selected one
    if (!avatar && data?.id) {
      avatar = await uploadAvatar({
        data: {
          crop: baseCrop,
          file: defaultAvatarImage,
          userId: data.id,
        },
      })
    }

    const profileData: UserType = {
      firstName: values?.firstName,
      lastName: values?.lastName,
      username: values?.username,
      address: {
        city: values?.city || '',
        state: values?.state || '',
      },
      bio: values?.bio || '',
      avatar,
      ...(onboardingView
        ? {}
        : {
            address: {
              city: values?.city || '',
              state: values?.state || '',
              address: values?.address || '',
              zipcode: values?.zipcode || '',
            },
            phone: values?.phone || '',
          }),
    }

    try {
      if (onboardingView && isChallengeToggleEnabled) {
        await addChallengeMembership()
      }

      await update.mutateAsync({ data: profileData })
      // If we're viewing this via the onboarding flow, we don't want to navigate
      if (!onboardingView) {
        navigation.navigate(links.PROFILE_PAGE)
      }
      setShowBusyIndicator(false)
    } catch (err) {
      setShowBusyIndicator(false)
      // Todo - handle error on the form level.
    }
  }

  return (
    <Page
      isLoading={showBusyIndicator}
      withNav={!onboardingView}
      withScroll
      withBack={!onboardingView}
      title={!onboardingView ? 'Edit Profile' : ''}
      navigation={navigation}
      backgroundColor={onboardingView ? 'greyDarkest' : 'white'}
      backgroundImageSource={onboardingView ? maskedBgImage : undefined}
    >
      {onboardingView ? (
        <Box padding={{ vertical: IS_WEB ? 'lg' : 'sm' }}>
          <OnBoardingView onSubmit={handleSubmit} />
        </Box>
      ) : (
        <>
          <Separator />
          <Box backgroundColor='white' padding={{ vertical: 'lg', horizontal: 'md' }}>
            <Box>
              <AvatarContainer>
                <EditableAvatar width={100} height={100} url={defaultAvatarImage.url} />
              </AvatarContainer>
            </Box>
            <EditProfileForm
              submitCopy={onboardingView ? 'Continue' : undefined}
              simpleView={onboardingView}
              initialValues={{
                ...data,
                address: data?.address?.address,
                city: data?.address?.city,
                phone: data?.phone || undefined,
                state: data?.address?.state,
                zipcode: data?.address?.zipcode,
              }}
              onSubmit={(values) => handleSubmit(values, false)}
            />
          </Box>
        </>
      )}
    </Page>
  )
}
